import React, { useEffect, useState } from "react";
import { Alert, Button, Col, Row } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api/client";
import EmptyState from "../components/common/EmptyState";
import LoadingState from "../components/common/LoadingState";
import PageHeader from "../components/common/PageHeader";
import { downloadApiFile } from "../utils/download";

function getPMEngagementStatusBadgeClass(status) {
  switch (status) {
    case "Completed":
      return "status-badge status-badge-completed";
    case "Incomplete":
      return "status-badge status-badge-incomplete";
    case "Not Done":
      return "status-badge status-badge-not-done";
    case "Ongoing":
    default:
      return "status-badge status-badge-ongoing";
  }
}

function PMVisitorLogsPage() {
  const history = useHistory();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchLogs();
  }, []);

  async function fetchLogs() {
    setLoading(true);
    setError("");

    try {
      const response = await api.get("/pm-visitor-logs");
      setLogs(response.data || []);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load PM visitor log entries.");
    } finally {
      setLoading(false);
    }
  }

  async function handleDownload() {
    setDownloading(true);

    try {
      await downloadApiFile("/pm-visitor-logs/export", {
        fallbackFileName: "pm-engagement.csv"
      });
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to download PM engagement log.");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="PM Engagement Log"
        action={
          <div className="d-flex gap-2">
            <Button variant="primary" onClick={() => history.push("/pm-visitor-logs/new")}>
              Add log entry
            </Button>
            <Button variant="outline-primary" onClick={handleDownload} disabled={downloading || loading} title="Download PM engagement">
              {downloading ? "Downloading..." : "Download"}
            </Button>
          </div>
        }
      />

      <Row className="g-4">
        <Col xs={12}>
          <div className="content-card table-card">
            <h5 className="mb-3">Engagement log entries</h5>
            {loading ? <LoadingState label="Loading PM visitor log..." /> : null}
            {error ? <Alert variant="danger">{error}</Alert> : null}
            {!loading && !error && !logs.length ? (
              <EmptyState title="No PM engagement log entries yet" text="The first engagement entry will appear here after you create it." />
            ) : null}

            {!loading && logs.length ? (
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th className="pm-log-date-cell">Date</th>
                      <th className="travel-request-status-cell">Status</th>
                      <th className="pm-log-team-cell">Team details</th>
                      <th className="pm-log-objective-cell">Objective of meeting</th>
                      <th className="pm-log-action-cell">Action point</th>
                      <th className="pm-log-person-cell">Responsible person</th>
                      <th className="pm-log-date-cell">Date due</th>
                      <th className="text-end pm-log-view-cell">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr key={log.uuid}>
                        <td className="pm-log-date-cell">{log.date || "-"}</td>
                        <td className="travel-request-status-cell">
                          <span className={getPMEngagementStatusBadgeClass(log.status)}>{log.status || "Ongoing"}</span>
                        </td>
                        <td className="pm-log-team-cell">{log.team_details || "-"}</td>
                        <td className="pm-log-objective-cell">{log.objective_of_meeting || "-"}</td>
                        <td className="pm-log-action-cell">{log.action_point || "-"}</td>
                        <td className="pm-log-person-cell">{log.responsible_person || "-"}</td>
                        <td className="pm-log-date-cell">{log.date_due || "-"}</td>
                        <td className="text-end pm-log-view-cell">
                          <Button variant="outline-secondary" size="sm" onClick={() => history.push(`/pm-visitor-logs/${log.uuid}`)}>
                            View details
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </Col>
      </Row>
    </div>
  );
}

export default PMVisitorLogsPage;
